import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { checkoutOutcome, deferDoorStep, type CheckoutOutcome } from "../lib/api";
import { loadHeldKnock, loadKnockTarget } from "../lib/draft";
import { meQuery, onboardingQuery, qk, threadsQuery } from "../lib/queries";
import { track } from "../lib/telemetry";
import { setShellPhase, type HomePhase } from "../lib/shell";
import { SignInScreen } from "../components/onboarding/sign-in-screen";
import { OnboardingDoorStep } from "../components/onboarding/door-step";
import { OnboardingEmailStep } from "../components/onboarding/email-step";
import { OnboardingPerk } from "../components/onboarding/perk";
import { OnboardingSplash } from "../components/onboarding/steps";
import { AppShell } from "../components/shell/app-shell";
import type { Overlay } from "../components/shell/state";

/**
 * `/` — the product, and the whole of onboarding (rulings C5, R-0826-3).
 *
 * THREE THINGS CAN BE TRUE AT THIS ADDRESS. Nobody is signed in, and step 1 is
 * the whole page. Somebody is signed in mid-run, and the step they owe is. Or
 * the run is finished and the shell is.
 *
 * WHY ONBOARDING IS AT AN ADDRESS THAT IS NOT ITS OWN. A step is not a place
 * somebody arrives at from outside — it is where the product happens to be for
 * this person, derived from server flags on every render (FR-1.2 #7). Giving
 * the steps URLs would let the Back button put the run into a state nobody
 * navigated to, and would need a stored step number to defend against it, which
 * is the exact thing the flags exist instead of.
 *
 * THE ORDER OF THE GATES IS THE PRODUCT DECISION, and it is why the email step
 * and the money step are both read before the knock target. A knocker who came
 * for somebody else's door still gives Nob an address and answers the plan
 * question first: the address is where a reply to their own knock will land
 * (R-0826-3), and the plan is what pays for the door's Nob to read what they
 * wrote (§2.3.2 #3) — and the step that asks names the knock. Only once it is answered does the shell open, with a knock at the
 * door the visit was always for, and the door step is stepped aside from on the
 * way (§2.3.3). The knock USED to be answered by navigating back out to
 * `/@handle`; it is a conversation in the console now (owner, 2026-08-26), so
 * this address is where the visit ends rather than a place it passes through.
 *
 * The two round trips that leave the origin still land here — the identity
 * provider and Stripe — and the held-draft machinery in `lib/draft` is what
 * carries the visit across both.
 */
export function HomeRoute() {
  const queryClient = useQueryClient();

  const { data: me } = useQuery(meQuery());
  const run = useQuery({ ...onboardingQuery(), enabled: Boolean(me) });
  /**
   * HAS THIS PERSON ALREADY KNOCKED? — the one question that tells a knocker
   * from an owner mid-run, asked of the server (gauntlet walk 2026-09-13 round
   * two, and ruling C5).
   *
   * The door step below used to decide it from `defer_rules` and a
   * `localStorage` key, and both are properties of A BROWSER rather than of a
   * person: the deferral is written by this component when the visit arrives
   * holding a knock target, so a knocker who signs in on a second device, who
   * cleared their storage, or whose session was opened any other way than
   * through the door poster comes back an owner mid-run — and is asked to hang
   * a door they never asked for, every time they open `/`.
   *
   * A conversation is the server's own record that this person is here for
   * somebody else's door. The shell reads the same list a moment later, so this
   * costs no second request; the only cost is that the door step waits for the
   * answer, which is why the wait below is gated on the step being owed at all.
   * An unreachable list is not evidence of anything — `retry: false`, and a
   * refusal leaves the run exactly where it was.
   */
  const threads = useQuery({ ...threadsQuery(), enabled: Boolean(me), retry: false });

  const [phase, setPhase] = useState<HomePhase>("loading");
  const [error, setError] = useState<string | null>(null);

  /** Read on the first render, before anything strips it off the URL. */
  const returning = useRef<CheckoutOutcome>(checkoutOutcome());
  /**
   * The door this visit came for, read ONCE, on the first render.
   *
   * The shell CONSUMES the stored target — it opens a conversation at that door
   * and clears the key — so every later reader on this screen has to be reading
   * this captured value rather than storage. The deferral below is the one that
   * would otherwise be lost: React runs the shell's effect before this
   * component's, so by the time the debt is recorded the key is already gone.
   */
  const target = useRef<string | null | undefined>(undefined);
  if (target.current === undefined) target.current = loadKnockTarget();
  const knockingAt = target.current;
  /** What the shell should be showing the moment it mounts. */
  const [overlay, setOverlay] = useState<Overlay | undefined>(undefined);
  /**
   * The door step, resumed from the console's pinned row.
   *
   * It is client state on purpose. The server's `defer_rules` stays true until
   * the step actually COMPLETES, so somebody who opens the screen, thinks
   * better of it and reloads lands back in the console rather than being held
   * on a step they already declined once.
   */
  const [resuming, setResuming] = useState(false);

  useEffect(() => setShellPhase(phase), [phase]);

  /**
   * The phase follows WHO IS SIGNED IN, not only how the screen was entered:
   * signing out from the shell's footer re-renders without a remount, so the
   * boot effect never runs again. `null` is the server's definitive "nobody";
   * `undefined` is a cache miss mid-refetch and means nothing at all.
   */
  useEffect(() => {
    if (me === null && phase !== "signin" && phase !== "loading") setPhase("signin");
  }, [me, phase]);

  useEffect(() => {
    if (phase !== "loading") return;
    let live = true;
    void (async () => {
      try {
        const who = await queryClient.ensureQueryData(meQuery());
        if (!live) return;
        setPhase(who ? "doorman" : "signin");
      } catch {
        if (!live) return;
        setPhase("signin");
        setError("Nob is not answering. Is the API running?");
      }
    })();
    return () => {
      live = false;
    };
  }, [phase, queryClient]);

  const view = run.data ?? null;
  const flags = view?.onboarding ?? null;
  const unfinished =
    flags !== null &&
    (flags.email_pending || flags.perk_pending || (flags.rules_pending && !flags.defer_rules));

  /**
   * Coming back from checkout is answered in the plan panel — but not in the
   * middle of a run. A trial started at the money step comes back to this
   * address too, and opening that panel over the door step would answer a
   * question nobody asked. Once, when the run is over.
   */
  const announced = useRef(false);
  useEffect(() => {
    if (announced.current || flags === null) return;
    announced.current = true;
    if (returning.current && !unfinished) setOverlay({ kind: "billing", view: "home" });
  }, [flags, unfinished]);

  /**
   * The door step, stepped aside from (§2.3.3). Recorded the moment the money
   * step is behind them and a knock is still waiting — before the visit goes
   * back to the door, so the debt survives the knock being sent.
   */
  const deferred = useRef(false);
  useEffect(() => {
    if (deferred.current || flags === null) return;
    if (flags.email_pending || flags.perk_pending) return;
    if (!flags.rules_pending || flags.defer_rules) return;
    if (knockingAt === null) return;
    deferred.current = true;
    void deferDoorStep().then(async (r) => {
      if (!r.ok) return;
      track({ name: "onboarding_door_deferred" });
      await queryClient.invalidateQueries({ queryKey: qk.onboarding() });
    });
  }, [flags, queryClient]);

  if (phase === "loading") return <OnboardingSplash />;

  if (phase === "signin") return <SignInScreen key="signin" error={error} />;

  // Signed in, and the run has not answered yet. The splash is the run's own
  // loading state; a blank frame here reads as a product that has not started.
  if (run.isPending || view === null) return <OnboardingSplash />;
  const flagsNow = view.onboarding;

  // STEP 2, EMAIL (ruling R-0826-3). Before the money step, and before the knock
  // target, for the same reason the money step is: the first thing that follows
  // a card is a receipt, and a receipt with nowhere to go is the one obligation
  // this product cannot discharge quietly. It is also the only step in the run
  // with no way past it — there is no skip, no defer and no knock that outranks
  // it, because every one of those would leave an account Nob cannot write to.
  if (flagsNow.email_pending && me) {
    return (
      <OnboardingEmailStep
        key="email"
        me={me}
        knocker={knockingAt !== null}
        // The first screen that can say it (owner item 3): Connect has answered
        // with the identity, so the run knows whose handle this is and the
        // server has already counted who is standing at it.
        waitingCount={view.waiting.count}
        // The server's own words for the age attestation (R-0914-1 (1)). It
        // rides on the run state for the same reason the starter vocabulary
        // does: the screen shows these bytes and hashes these bytes.
        attestation={view.age_attestation}
        // An owner walks on to the money step, a knocker to the same one. Both
        // are decided on the next render off the re-read flags — this only has
        // to make sure there IS a next render.
        onConfirmed={() => setResuming(false)}
      />
    );
  }

  // STEP 3. It comes before the knock target on purpose: the plan is what pays
  // for the door's Nob to read what this person wrote.
  if (flagsNow.perk_pending && me) {
    const held = loadHeldKnock();
    return (
      <OnboardingPerk
        key="perk"
        me={me}
        heldHandle={knockingAt ? (held?.handle ?? knockingAt) : null}
        // A knocker goes back to the door; an owner walks on to step 3. Both
        // are decided on the next render, off the re-read flags — this only
        // has to make sure there IS a next render.
        onAnswered={() => setResuming(false)}
      />
    );
  }

  // STEP 4, either as the owner's run or as the debt a knocker came back for.
  //
  // THE DOOR ALREADY EXISTS HERE (ruling R-1006-2): the server hung it with the
  // starter rules when the email step finished, so this step TUNES it, and a
  // knocker who defers it still has a live door.
  //
  // A KNOCK STILL OUTRANKS IT, and that is what `!knockingAt` is doing here.
  // The deferral above is a POST and takes a moment; without this clause the
  // step would flash on screen in the gap between the write and the re-read,
  // asking somebody who came here to say something to somebody else to go and
  // hang a door first. It used to be a splash over the same gap, back when this
  // address answered a knock by navigating away from itself.
  //
  // AND A PERSON WHO HAS ALREADY KNOCKED IS NOT MID-RUN. Their run ended where
  // they meant it to end — at somebody else's door — and what they came back
  // to `/` for is the conversation, not a door of their own. The wizard waits
  // for that answer rather than guessing: the flash it would otherwise put on
  // screen is the same one `!knockingAt` exists to prevent, one round trip
  // later. `resuming` is the way in for anybody who does want the step, and it
  // is the pinned row in the console that sets it.
  const owesDoorStep = flagsNow.rules_pending && !flagsNow.defer_rules && !knockingAt;
  if (owesDoorStep && threads.isPending) return <OnboardingSplash />;
  const hasKnocked = (threads.data?.length ?? 0) > 0;
  if ((owesDoorStep && !hasKnocked) || (resuming && flagsNow.rules_pending)) {
    return (
      <OnboardingDoorStep
        key="rules"
        starter={view.starter}
        waitingCount={view.waiting.count}
        handle={me?.xHandle ?? null}
        /**
         * WHICH RUN THIS PERSON WAS COUNTED THROUGH, for the step counter.
         *
         * `defer_rules` is the server's own record that the door step was
         * stepped aside from (§2.3.3), and stepping aside is the knocker's
         * lane: the deferral above fires for a visit that arrived holding a
         * knock, without this screen ever being drawn. An owner walking the run
         * straight through has it false and gets the four-step count they were
         * shown all along.
         *
         * THE ONE CASE IT CANNOT TELL APART is an owner who pressed the screen's
         * own quiet Skip and came back later: the server records the same flag
         * for both, and nothing durable says which lane wrote it. They would be
         * counted as a knocker on the return. Distinguishing them needs the
         * deferral itself to say where it came from, which is a field on the
         * wire and not a thing this screen can derive.
         */
        knocker={flagsNow.defer_rules}
        onHung={() => setResuming(false)}
        // The quiet way past (owner, 2026-08-27): the same defer the knocker
        // path records, chosen by hand. The debt survives — the pinned Nob row
        // and the setup shelf are the ways back.
        onSkip={() => {
          setResuming(false);
          void deferDoorStep().then(async (r) => {
            if (!r.ok) return;
            track({ name: "onboarding_door_deferred" });
            await queryClient.invalidateQueries({ queryKey: qk.onboarding() });
          });
        }}
      />
    );
  }

  /**
   * THE KNOCK HAPPENS IN THE CONSOLE NOW (owner, 2026-08-26). This address used
   * to answer a stored knock target by navigating back out to `/@handle` and
   * the door poster; somebody who is signed in has no use for that surface, so
   * the shell opens a conversation at the door instead and the visit never
   * leaves the console it just arrived in.
   */
  return (
    <AppShell
      initialOverlay={overlay}
      paid={returning.current}
      composeAt={knockingAt}
      onSetUpDoor={flagsNow.rules_pending ? () => setResuming(true) : undefined}
    />
  );
}
